import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { Avatar } from '../../components/Avatar';
import { Button } from '../../components/Button';
import { TextArea } from '../../components/Field';
import { Paper } from '../../components/Paper';
import { ErrorNotice } from '../../components/States';
import { useToast } from '../../components/Toast';
import { createPost, editPost, uploadPhoto, type Category } from '../../lib/api';
import { CATEGORIES } from '../../lib/categories';
import { useMe } from '../auth/session';
import { PhotoPicker } from '../photos/PhotoPicker';
import type { PreparedPhoto } from '../photos/prepare';
import { useRefreshBoards } from './queries';
import styles from './Composer.module.css';
import { FEED_REACH } from '../../../shared/region';

const MAX = 2000;

const CHIP_COLORS: Record<Category, string> = {
  note: '#72876a',
  question: 'var(--sky)',
  alert: 'var(--adobe)',
  lost_found: '#b98322',
  recommendation: 'var(--forest)',
};

interface Props {
  /** Start expanded (the dedicated Post page) instead of as a one-line prompt. */
  startOpen?: boolean;
  /** Edit an existing note instead of writing a new one. */
  editing?: { id: string; body: string; category: Category | null };
  onDone?: (postId: string) => void;
  onCancel?: () => void;
}

export function Composer({ startOpen = false, editing, onDone, onCancel }: Props) {
  const { data: me } = useMe();
  const toast = useToast();
  const refresh = useRefreshBoards();
  const [open, setOpen] = useState(startOpen || Boolean(editing));
  const [body, setBody] = useState(editing?.body ?? '');
  const [category, setCategory] = useState<Category | null>(editing?.category ?? null);
  const [photos, setPhotos] = useState<PreparedPhoto[]>([]);
  const [progress, setProgress] = useState<string | null>(null);

  // Free preview images if the composer goes away with photos still attached.
  const photosRef = useRef(photos);
  useEffect(() => {
    photosRef.current = photos;
  });
  useEffect(() => () => photosRef.current.forEach((p) => URL.revokeObjectURL(p.previewUrl)), []);

  const clearPhotos = () => {
    photos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    setPhotos([]);
  };

  const save = useMutation({
    mutationFn: async () => {
      if (editing) {
        await editPost(editing.id, body, category);
        return editing.id;
      }
      const ids: string[] = [];
      for (const [i, photo] of photos.entries()) {
        setProgress(photos.length > 1 ? `Uploading photo ${i + 1} of ${photos.length}…` : 'Uploading photo…');
        ids.push((await uploadPhoto(photo.blob)).id);
      }
      setProgress(null);
      return createPost(body, category, ids);
    },
    onSettled: () => setProgress(null),
    onSuccess: async (postId) => {
      toast.show(editing ? 'Note updated' : 'Posted to your board');
      setBody('');
      setCategory(null);
      clearPhotos();
      if (!startOpen && !editing) setOpen(false);
      await refresh();
      onDone?.(postId);
    },
  });

  if (me?.status === 'suspended') {
    return (
      <Paper>
        <p>Your account is suspended, so you can read the board but can't post right now.</p>
      </Paper>
    );
  }

  if (!open) {
    return (
      <Paper padded={false} className={styles.composer}>
        <button type="button" className={styles.prompt} onClick={() => setOpen(true)}>
          <Avatar name={me?.displayName ?? null} hue={me?.avatarHue ?? 30} size={32} />
          What's happening nearby?
        </button>
      </Paper>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (body.trim()) save.mutate();
  };

  const cancel = () => {
    setBody(editing?.body ?? '');
    setCategory(editing?.category ?? null);
    clearPhotos();
    save.reset();
    if (onCancel) onCancel();
    else setOpen(false);
  };

  return (
    <Paper as="form" padded={false} className={styles.composer} onSubmit={submit}>
      <TextArea
        label={editing ? 'Edit your note' : "What's happening nearby?"}
        hideLabel={!editing}
        placeholder="What's happening nearby?"
        value={body}
        maxLength={MAX}
        rows={startOpen ? 7 : 4}
        // Opening the composer is an explicit request to type.
        autoFocus
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(e);
        }}
      />
      <fieldset className={styles.labels}>
        <legend className={styles.labelsLegend}>Add a label (optional)</legend>
        {CATEGORIES.map((c) => (
          <label
            key={c.value}
            className={styles.chip}
            title={c.hint}
            style={{ '--chip-color': CHIP_COLORS[c.value] } as CSSProperties}
          >
            <input
              type="radio"
              name="category"
              value={c.value}
              checked={category === c.value}
              onChange={() => setCategory(c.value)}
              onClick={() => category === c.value && setCategory(null)}
            />
            <span>{c.label}</span>
          </label>
        ))}
      </fieldset>
      {!editing && <PhotoPicker photos={photos} onChange={setPhotos} disabled={save.isPending} />}
      {save.error && <ErrorNotice error={save.error} />}
      <div className={styles.row}>
        {!editing && <span className={styles.reach}>Neighbors within {FEED_REACH} of home will see this.</span>}
        <div className={styles.buttons}>
          <Button variant="quiet" onClick={cancel}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!body.trim() || body.length > MAX || save.isPending}>
            {editing ? 'Save changes' : save.isPending ? (progress ?? 'Posting…') : 'Post'}
          </Button>
        </div>
      </div>
    </Paper>
  );
}
